import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Tab, TabList, Tabs, Typography } from '@mui/joy';
import { useSearchParams } from 'react-router-dom';
import { CashLedgerTab } from '../components/CashLedgerTab';
import { WageDeductionsTab } from '../components/WageDeductionsTab';
import type { LedgerPerson } from '../ledger.model';
import { ledgerService } from '../ledger.service';

type LedgerTab = 'cash' | 'deductions';

/** Finance → Ledger: cash held outside the bank, and personal items deducted from directors' wages. */
const LedgerPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const tab: LedgerTab = params.get('tab') === 'deductions' ? 'deductions' : 'cash';
  const [people, setPeople] = useState<LedgerPerson[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    ledgerService.listPeople()
      .then(setPeople)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load the ledger'));
  }, []);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280 }}>
      <Stack spacing={2.5}>
        <Stack spacing={0.5}>
          <Typography level="h3">Ledger</Typography>
          <Typography level="body-sm" textColor="neutral.600" sx={{ maxWidth: 760 }}>
            Cash kept outside the bank, and personal purchases to be taken from wages at month end.
          </Typography>
        </Stack>

        {error ? (
          <Alert color="danger">{error}</Alert>
        ) : people == null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : people.length === 0 ? (
          <Alert color="neutral">The ledger is only available to the directors.</Alert>
        ) : (
          <>
            <Tabs
              value={tab}
              onChange={(_, value) => setParams(value === 'deductions' ? { tab: 'deductions' } : {}, { replace: true })}
              sx={{ bgcolor: 'transparent' }}
            >
              <TabList>
                <Tab value="cash">Cash</Tab>
                <Tab value="deductions">Wage deductions</Tab>
              </TabList>
            </Tabs>
            {tab === 'cash' ? <CashLedgerTab /> : <WageDeductionsTab people={people} />}
          </>
        )}
      </Stack>
    </Box>
  );
};

export default LedgerPage;
